<template>
    <div class="project-home w-full rows-min-minmax _p-1.5">
        <!-- Header -->
        <div class="f-x-between _mb-1.5">
            <!-- Me -->
            <user-thumb :user="profile"
                        :position="rolesAsLabel"
                        align="center"
                        primary-name
                        full-name
                        with-avatar
                        with-position />

            <div class="f-y-center space-x-0.5">
                <!-- Actions -->
                <el-dropdown 
                    :disabled="!hasActions"
                    trigger="click"
                    @command="act">
                    <!-- Trigger -->
                    <el-button ref="actions"
                               type="primary"
                               size="default"
                               round>
                        {{ labels.actions }}
                        <i class="el-icon-arrow-down" />
                    </el-button>

                    <!-- List -->
                    <el-dropdown-menu slot="dropdown"
                                      class="max-h-1/4 overflow-auto with-custom-scrollbar">
                        <el-dropdown-item v-for="action in actions"
                                          :key="action.id"
                                          :command="action.id">
                            {{ action.label }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>

                <!-- Notification -->
                <el-dropdown v-loading="creatableNotificationTypesLoading"
                             :disabled="!hasNotificationTypes"
                             trigger="click"
                             @command="notify">
                    <!-- Trigger -->
                    <el-button ref="notify"
                               type="primary"
                               size="default"
                               icon="el-icon-warning-outline"
                               plain
                               round>
                        {{ labels.notify }}
                        <i class="el-icon-arrow-down" />
                    </el-button>

                    <!-- List -->
                    <el-dropdown-menu slot="dropdown"
                                      class="max-h-1/4 overflow-auto with-custom-scrollbar">
                        <el-dropdown-item v-for="type in notificationTypesAsOptions"
                                          :key="type.value"
                                          :command="type.value">
                            {{ type.label }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
                
                <!-- Schedule -->
                <el-button ref="schedule"
                           type="primary"
                           size="default"
                           icon="el-icon-date"
                           plain
                           round
                           @click="showSchedule">
                    {{ labels.schedule }}
                </el-button>

                <!-- Help -->
                <el-button ref="help"
                           class="m-0"
                           type="primary"
                           size="default"
                           icon="el-icon-notebook-1"
                           plain
                           round
                           @click="goToHelp">
                    {{ labels.help }}
                </el-button>
            </div>
        </div>

        <!-- Body -->
        <div class="rel rows-min-minmax rounded border-1 border-gray-100 hidden-y">
            <!-- Tabs -->
            <el-tabs v-model="selectedTab"
                     class="project-home__tabs"
                     @tab-click="goTo">
                <!-- Tasks -->
                <el-tab-pane :label="labels.tasks"
                             name="project.home.my-tasks" />
                <!-- Timeline -->
                <el-tab-pane :label="labels.activity"
                             name="project.home.activity" /> 
                <!-- Photos -->
                <el-tab-pane :label="labels.photos"
                             name="project.home.photos" />
            </el-tabs>

            <router-view ref="child" />
        </div>

        <!-- Schedule -->
        <user-schedule-dialog 
            v-if="scheduleDisplayed"
            :id="profileId"
            :description="labels.description"
            :schedule-description="labels.scheduleDescription"
            @close="hideSchedule" />

        <!-- Tutorial -->
        <tutorial ref="tutorial"
                  type="project_main_page"
                  :steps="tutorialSteps" />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { HELPER_DEFAULT_URL } from '@/utils/helper'
import { openNew } from '@/utils/browser'
import { sortAsName } from '@/utils/immutable'

import { resourceable } from '@/store/connectors'

import { notificationTypes } from '@/models/notifications'
import { types } from '@/models/tasks'

import Tutorial from '@/components/tutorials/Tutorial.vue'
import UserThumb from '@/components/users/UserThumb'
import UserScheduleDialog from '@/components/users/UserScheduleDialog'

const labels = {
  help: 'Инструкции',
  notify: 'Сообщить о проблеме',
  actions: 'Мои действия',
  tasks: 'Мои задачи',
  photos: 'Фотографии',
  activity: 'Последние события',
  schedule: 'Расписание',
  description: 'Настройте свою работу в проекте',
  scheduleDescription: 'Проставьте ячейки рабочего времени и нажмите "Сохранить"',
  taskTools: 'Фильтр задач',
  tutorialLaunch: 'Обучающий тур',

  viewMode: {
    list: 'Список',
    calendar: 'Календарь'
  },

  calendarViews: {
    dayGridMonth: 'Месяц',
    dayGridWeek: 'Неделя'
  },

  tutorial: {
    actions: 'Добавляйте новые задачи и запускайте процессы',
    notify: 'Уведомляйте о проблемах или критичных изменениях на объекте',
    help: 'Ознакомьтесь с подробной информацией о работе с платформой',
    tasks: 'Управляйте своими задачами',
    taskTools: 'Изменяйте условия отображения задач в соответствии с требованиями',
    calendar: 'Просматривайте свои задачи в режиме планировщика',
    schedule: 'Настройте свое расписания для участия в процессах строительного контроля',
    tutorialLaunch: 'Запустите обучающий тур для выбранной странице еще раз'
  }
}

export default {
  components: {
    Tutorial,
    UserThumb,
    UserScheduleDialog
  },
  mixins: [
    resourceable({ on: 'notifications', name: 'creatableNotificationTypes', mounted: true }),
    resourceable({ on: 'account', name: 'creatableTasks', mounted: true }),
    resourceable({ on: 'account', name: 'access' })
  ],
  data() {
    return {
      selectedTab: null,
      scheduleDisplayed: false,
      
      labels
    }
  },
  computed: {
    ...mapGetters('auth', [ 'profile', 'profileId' ]),
    ...mapGetters('account', [ 'hasAccess' ]),

    rolesAsLabel() {
      return this.access?.roles_description || ''
    },

    notificationTypesAsOptions() {
      return this.creatableNotificationTypes.map(({ type, translated_type, is_available_action }) => ({
        value: type,
        label: translated_type,
        allowed: is_available_action
      })).filter(({ allowed }) => allowed)
    },

    hasNotificationTypes() {
      return !!this.notificationTypesAsOptions.length
    },

    actions() {
      return [
        ...Object.values(this.creatableTasks)
          .filter(({ creatable }) => creatable)
          .map(({ type }) => ({
            label: ({
              [types.DEFECTS_AND_VIOLATIONS]: 'Создать предписание',
              [types.PHOTO_ORDER]: 'Заказать снимки 360',
              [types.ACCEPTANCE_OF_WORK]: 'Вызвать стройконтроль'
            })[type],
            act: () => this.$router.push({ name: 'project.tasks.create', query: { type } })
          })),
        ...[
          this.hasAccess({ permissions: ['project_approval_process_be_project_designer'] }) && {
            label: 'Создать процесс согласования',
            act: () => this.createApproval()
          }
        ]
      ]
        .map(x => ({ id: key(), ...x }))
        .filter(({ label }) => label)
        .sort(sortAsName(x => x.label))
    },

    hasActions() {
      return !!this.actions.length
    },

    tutorialSteps() {
      return [
        {
          name: 'actions',
          target: () => this.$refs?.actions?.$el
        },
        {
          name: 'notify',
          target: () => this.$refs?.notify?.$el
        },
        {
          name: 'help',
          target: () => this.$refs?.help?.$el
        },
        {
          name: 'tasks',
          target: () => this.$refs?.child?.$refs?.tasks,
          before: () => this.$refs?.child?.changeTasksView('list')
        },
        {
          name: 'taskTools',
          target: () => this.$refs?.child?.$refs?.taskTools,
          before: () => this.$refs?.child?.changeTasksView('list')
        },
        {
          name: 'calendar',
          target: () => this.$refs?.child?.$refs?.tasks,
          before: () => this.$refs?.child?.changeTasksView('calendar')
        },
        {
          name: 'schedule',
          target: () => this.$refs?.schedule?.$el
        },
        {
          name: 'tutorialLaunch',
          target: () => document.getElementById('tutorial-launch'),
          noOffset: true
        }
      ].map(x => ({ 
        ...x, 
        title: labels[x.name], 
        description: labels.tutorial[x.name] 
      }))
    }
  },
  mounted() {
    this.selectedTab = this.$route.name

    this.$refs.tutorial.show()
    this.$route.meta?.tutorial?.onShow?.(() => this.$refs.tutorial?.showForce?.())
  },
  methods: {
    ...mapActions('project', ['createProjectDocument']),
    ...mapMutations({ showForm: 'form/SHOW_FORM' }),

    notify(type) {
      const name = ({
        [notificationTypes.DPW]: 'project.notifications.dpw',
        [notificationTypes.LCR]: 'project.notifications.lcr'
      })[type]

      this.$router.push({ name })
    },

    act(id) {
      const action = this.actions.find(x => x.id === id)

      const { act } = action || {}

      act?.()
    },

    goToHelp() {
      openNew(HELPER_DEFAULT_URL)
    },

    goTo({ name }) {
      this.$router.push({ name })
    },

    showSchedule() {
      this.scheduleDisplayed = true
    },

    hideSchedule() {
      this.scheduleDisplayed = false
    },

    createApproval() {
      this.showForm({
        formName: 'task-bar-form',
        formTitle: 'Документ на согласование',
        action: this.createProjectDocument,
        callback: () => {}
      });
    }
  }
}
</script>

<style lang="scss">
.project-home {
  &__tabs {
    .el-tabs__header {
      margin-bottom: 0;

      .el-tabs__nav-scroll {
        padding: 0 1rem;

        .el-tabs__nav {
          padding: 0.8rem 0;
        }
      }
    }
  }
}
</style>
